import { GenericDropdownContext } from "@studio3t/generic-components";
import {
  type MouseEvent as ReactMouseEvent,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from "react";

// A click that closes an open dropdown must not also act. The library
// dropdown toggles and dismisses on POINTERDOWN, so by the time the CLICK
// reaches a table row the menu is already gone and the row would open its
// pipeline — leaving a user nowhere safe to click just to close a menu.
// The guard decides at pointerdown, from a capture listener on the window:
// that runs before the dropdown's own document listener (and before React
// has flushed the "closed" state it triggers), so it still sees the menu
// open. A press inside the container while a menu is open marks the click
// that follows as spent, and that click is stopped in the container's
// capture phase before any handler below sees it. Presses on a dropdown
// trigger are let through (toggling this menu, or opening another row's,
// is the intent there); the dropdown's own menu lives in a portal outside
// the container, so its items are never affected.

/** Put on every dropdown trigger inside a guarded container. */
export const DROPDOWN_TRIGGER_ATTR = {
  "data-dropdown-trigger": "true",
} as const;

function isDropdownTrigger(target: EventTarget | null): boolean {
  return (
    target instanceof Element &&
    target.closest("[data-dropdown-trigger]") !== null
  );
}

export function useDropdownDismissGuard<T extends HTMLElement>() {
  const containerRef = useRef<T>(null);
  const openCount = useRef(0);
  const spendNextClick = useRef(false);

  const onDropdownOpenChange = useCallback((open: boolean) => {
    openCount.current += open ? 1 : -1;
  }, []);

  useEffect(() => {
    // Re-decided on every press: a press that never becomes a click (a
    // drag out) can't spend the next real one.
    const onPointerDown = (e: PointerEvent) => {
      spendNextClick.current =
        openCount.current > 0 &&
        e.target instanceof Node &&
        containerRef.current?.contains(e.target) === true &&
        !isDropdownTrigger(e.target);
    };
    window.addEventListener("pointerdown", onPointerDown, true);
    return () => window.removeEventListener("pointerdown", onPointerDown, true);
  }, []);

  const onClickCapture = useCallback((e: ReactMouseEvent) => {
    if (!spendNextClick.current) return;
    spendNextClick.current = false;
    e.stopPropagation();
  }, []);

  return { onDropdownOpenChange, containerRef, onClickCapture };
}

/** Rendered inside a dropdown's trigger: reports the menu's open state to
 *  the guard (the library exposes it only through this context). */
export function DropdownOpenReporter({
  onOpenChange,
}: {
  onOpenChange?: (open: boolean) => void;
}) {
  const { open } = useContext(GenericDropdownContext);
  useEffect(() => {
    if (!open || !onOpenChange) return;
    onOpenChange(true);
    return () => onOpenChange(false);
  }, [open, onOpenChange]);
  return null;
}
